import '@app/App.css';
import CompozzSpinner from '@comps/ui/CompozzSpinner';
import { isCompozzError } from '@dto/CompozzError';
import { VerifyRequest } from '@dto/login';
import { Workspace } from '@dto/workspaces';
import { BASE_URL, USER_ORG_ID, USERNAME } from '@utils/constants';
import { get, refreshToken } from '@utils/utils';
import { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useAppStore, useModelStore, useUserStore } from 'src/store';
import WorkspaceSection from './WorkspaceSection';
import AnnouncementsDialog from './announcements/AnnouncementsDialog';
import { filterActiveAnnouncements, getUnseenAnnouncements } from './announcements/announcementsLogic';
import { ANNOUNCEMENTS, Announcement } from './announcements/content/announcements';
import { getSeenAnnouncementIds, markAnnouncementsSeen } from './announcements/seenAnnouncements';
import Manual from './manual/Manual';
import UsersManagement from './users/UsersManagement';
import WorkspaceManagement from './workspaces/WorkspaceManagement';

function Home() {

  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [pendingAnnouncements, setPendingAnnouncements] = useState<Announcement[]>([]);
  const setWorkspaces = useModelStore((state) => state.updateWorkspaces);
  const fetchUsers = useAppStore(state => state.fetchUsers);
  const selectWorkspace = useModelStore((state) => state.selectWorkspace);
  const username = useUserStore(state => state.username) || localStorage.getItem(USERNAME);
  const orgId = useUserStore(state => state.orgId) || localStorage.getItem(USER_ORG_ID);
  const updateUsername = useUserStore(state => state.updateUsername);
  const updateOrgId = useUserStore(state => state.updateOrgId);
  const updateUserRole = useModelStore(state => state.updateUserRole);
  const fetchTenant = useAppStore(state => state.fetchTenant);
  const location = useLocation();

  const fetchWorkspaces = async () => {
    try {
      const response = await get<Workspace[]>(`${BASE_URL}/model/workspaces`);
      if (isCompozzError(response)) {
        throw response;
      }
      const workspaces = response as Workspace[];
      if (workspaces && workspaces.length > 0) {
        setWorkspaces(workspaces);
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      setWorkspaces([]);
    }
  };

  useEffect(() => {
    if (location.pathname !== '/login') {
      checkLoginStatus();
    }
    selectWorkspace(null);
    fetchTenant();
    fetchUsers();
    fetchWorkspaces();
  }, []);

  // Show the "What's new" dialog once per login for announcements the user has
  // not seen yet. The /manual guard avoids showing it in the manual tab, which
  // mounts its own Home instance.
  useEffect(() => {
    if (isLoggedIn !== true || location.pathname === '/manual') {
      return;
    }
    getSeenAnnouncementIds().then(seen =>
      setPendingAnnouncements(getUnseenAnnouncements(
        filterActiveAnnouncements(ANNOUNCEMENTS, new Date()), seen)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Reset workspace context when navigating to home page
  useEffect(() => {
    if (location.pathname === '/') {
      selectWorkspace(null);
    }
  }, [location.pathname, selectWorkspace]);


  function clearAuth() {
    updateUsername(undefined);
    updateOrgId(undefined);
    updateUserRole(null);
    setIsLoggedIn(false);
  }

  const checkLoginStatus = async () => {
    if (!username || !orgId) {
      clearAuth();
      return;
    }
    try {
      const verifyRequest: VerifyRequest = {
        username: username.toLowerCase(),
        orgId: orgId
      };
      // Replace this with your actual API endpoint
      const response = await fetch(`${BASE_URL}/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': 'https://localhost'
        },
        body: JSON.stringify(verifyRequest),
        credentials: 'include'
      });
      if (response.ok) {
        updateUsername(username.toLowerCase());
        updateOrgId(orgId);
        setIsLoggedIn(true);
        return;
      } else {
        refreshToken().then(success => {
          if (!success) {
            clearAuth();
          } else {
            updateUsername(username.toLowerCase());
            updateOrgId(orgId);
            setIsLoggedIn(true);
          }
        });
      }

    } catch (error) {
      console.error('Error checking login status:', error);
      clearAuth();
    }
  };

  if (isLoggedIn === null) {
    return (
      <div className="flex justify-center items-center h-full w-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <CompozzSpinner />
      </div>
    )
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<WorkspaceManagement />} />
        <Route path="/workspaces/:wsKey/*" element={<WorkspaceSection />} />
        <Route path="/users" element={<UsersManagement />} />
        <Route path="/manual" element={<Manual />} />
      </Routes>
      <AnnouncementsDialog
        open={pendingAnnouncements.length > 0}
        announcements={pendingAnnouncements}
        onClose={() => {
          markAnnouncementsSeen(pendingAnnouncements.map(a => a.id));
          setPendingAnnouncements([]);
        }}
      />
    </>
  );
};

export default Home;